/**
 * @fileoverview Chat selection and read-state handlers.
 * @module logic/chat/chatHandlers
 */

import { logger, sanitizeError } from "../../utils/logger.js";
import {
  getChatMessages,
  markChatAsRead,
  parseApiGatewayJsonBody,
} from "./chatApi.js";
import { SCROLL_INTO_VIEW_DELAY_MS } from "./chatConstants.js";

/**
 * Conditionally marks a chat as read if user is active and tab is visible.
 * @async
 * @param {string} chatId - Chat identifier
 * @param {string} lastMessageTimestamp - Timestamp of last message
 * @param {Function} navigate - React Router navigation function
 * @param {Object} isUserActiveRef - Ref tracking user activity
 * @param {Object} lastSeenMessageTimestampByChatRef - Ref tracking seen timestamps
 * @returns {Promise<void>}
 */
export const maybeMarkChatAsRead = async (
  chatId,
  lastMessageTimestamp,
  navigate,
  isUserActiveRef,
  lastSeenMessageTimestampByChatRef,
) => {
  try {
    logger.info("maybeMarkChatAsRead called", {
      data: {
        chatId,
        lastMessageTimestamp,
        visibility: document.visibilityState,
        isUserActive: isUserActiveRef.current,
        previousSeenTimestamp:
          lastSeenMessageTimestampByChatRef.current[chatId] || null,
      },
    });
    if (!chatId) {
      logger.info("markChatAsRead skip: no chatId");
      return;
    }
    if (document.visibilityState !== "visible" || !isUserActiveRef.current) {
      logger.info("markChatAsRead skip: tab not visible or user inactive", {
        data: {
          visibility: document.visibilityState,
          isUserActive: isUserActiveRef.current,
        },
      });
      return;
    }
    const lastMap = lastSeenMessageTimestampByChatRef.current;
    if (lastMap[chatId] && lastMap[chatId] === lastMessageTimestamp) {
      logger.info("markChatAsRead skip: already marked for this timestamp");
      return;
    }
    logger.info("markChatAsRead sending request", {
      data: {
        chatId,
        lastMessageTimestamp,
      },
    });
    const response = await markChatAsRead(chatId, navigate);
    logger.info("markChatAsRead response", { data: response });
    lastMap[chatId] = lastMessageTimestamp;
  } catch (error) {
    logger.error("Error marking chat as read:", { err: sanitizeError(error) });
  }
};

/**
 * Handles chat selection, loads messages, and marks as read.
 * @async
 * @param {Object} params
 * @param {Object} params.chat - Selected chat object
 * @param {Array} params.users - Array of user objects
 * @param {Object} params.companies - Companies data keyed by RFC
 * @param {Function} params.navigate - React Router navigation function
 * @param {Function} params.setLoading - State setter for loading indicator
 * @param {Function} params.setSelectedChat - State setter for selected chat
 * @param {Function} params.setSelectedUser - State setter for selected user
 * @param {Function} params.setSelectedCompany - State setter for selected company
 * @param {Function} params.setChatMessages - State setter for messages
 * @param {Function} params.setChats - State setter for chats list
 * @param {Function} params.extractMessageTimestampFn - Timestamp extraction function
 * @param {Function} params.maybeMarkChatAsReadFn - Mark as read function
 * @param {Object} params.isUserActiveRef - Ref tracking user activity
 * @param {Object} params.lastSeenMessageTimestampByChatRef - Ref tracking seen timestamps
 * @param {Object} params.messageEndRef - Ref for scrolling to end of messages
 * @returns {Promise<void>}
 */
export const handleChatSelect = async ({
  chat,
  users,
  companies,
  navigate,
  setLoading,
  setSelectedChat,
  setSelectedUser,
  setSelectedCompany,
  setChatMessages,
  setChats,
  extractMessageTimestampFn,
  maybeMarkChatAsReadFn,
  isUserActiveRef,
  lastSeenMessageTimestampByChatRef,
  messageEndRef,
}) => {
  setLoading(true);
  setSelectedChat(chat);
  logger.info("Selected chat:", { data: chat });
  const currentUserId = localStorage.getItem("userId");
  const otherUserId =
    chat.userAId === currentUserId ? chat.userBId : chat.userAId;

  const matchedUser = users.find((user) => user._id === otherUserId);
  if (matchedUser) {
    setSelectedUser(matchedUser);
    if (matchedUser.RFC && companies[matchedUser.RFC]) {
      setSelectedCompany(companies[matchedUser.RFC]);
    }
  }

  try {
    logger.info("Fetching chat messages for chat:", { data: chat.PK });
    const messages = await getChatMessages(chat.PK, navigate);
    const parsedBody = parseApiGatewayJsonBody(messages);
    if (parsedBody?.success && Array.isArray(parsedBody.data)) {
      const fetchedMessages = parsedBody.data;
      setChatMessages(fetchedMessages);
      logger.info("Chat messages:", { data: fetchedMessages });
      // Mark as read on open if last message is incoming and app is visible
      const last = fetchedMessages[fetchedMessages.length - 1];
      const isIncoming = last && last.senderUserId !== currentUserId;
      logger.info("On open mark-as-read check", {
        data: {
          isIncoming,
          lastTimestamp: extractMessageTimestampFn(last),
          visibility: document.visibilityState,
          isUserActive: isUserActiveRef.current,
          chatId: chat.PK,
        },
      });
      if (
        isIncoming &&
        document.visibilityState === "visible" &&
        isUserActiveRef.current
      ) {
        setChats((prev) =>
          prev.map((c) =>
            c.PK === chat.PK ? { ...c, lastMessageSeen: true } : c,
          ),
        );
        await maybeMarkChatAsReadFn(
          chat.PK,
          extractMessageTimestampFn(last),
          navigate,
          isUserActiveRef,
          lastSeenMessageTimestampByChatRef,
        );
      }
    } else {
      logger.info("No messages found or API returned empty data");
      setChatMessages([]);
    }
    setTimeout(() => {
      messageEndRef.current?.scrollIntoView({
        behavior: "instant",
        block: "end",
      });
    }, SCROLL_INTO_VIEW_DELAY_MS);
  } catch (error) {
    logger.error("Error fetching chat messages:", {
      err: sanitizeError(error),
    });
    setChatMessages([]);
  } finally {
    setLoading(false);
  }
};

/**
 * Gets the current user's profile image URL.
 * @param {Array} users - Array of user objects
 * @returns {string|null} Profile image URL or null
 */
export const getCurrentUserProfileImage = (users) => {
  const currentUserId = localStorage.getItem("userId");
  const currentUser = users.find((user) => user._id === currentUserId);
  return currentUser?.imagenDePerfil || null;
};
